import React from 'react';
import { AlertCircle, PlusCircle } from 'lucide-react';
import { FormulaUnblockingSuggestion } from '../../types';
import { VARIABLE_BY_ID } from '../../data/formulas';
import { InlineLatex } from './LatexRenderer';

interface FormulaUnblockingCardProps {
  selectedKnowns: string[];
  selectedTarget: string;
  unblockingGrandezas?: FormulaUnblockingSuggestion[];
  onAddKnown: (id: string) => void;
}

export const FormulaUnblockingCard: React.FC<FormulaUnblockingCardProps> = ({
  selectedKnowns,
  selectedTarget,
  unblockingGrandezas,
  onAddKnown,
}) => {
  return (
    <div className="rounded-xl border border-amber-300 bg-amber-50/70 p-5 dark:border-amber-800/80 dark:bg-amber-950/30 space-y-4">
      <div className="flex items-start gap-3">
        <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
        <div>
          <h3 className="font-serif text-sm font-bold text-amber-950 dark:text-amber-100">
            Dados insuficientes para deduzir {VARIABLE_BY_ID[selectedTarget]?.name} (
            <InlineLatex>{VARIABLE_BY_ID[selectedTarget]?.symbol ?? ''}</InlineLatex>)
          </h3>
          <p className="mt-1 text-xs leading-relaxed text-amber-900 dark:text-amber-300">
            As grandezas fornecidas (
            {selectedKnowns.map((id) => VARIABLE_BY_ID[id]?.name).join(', ')}) não formam uma cadeia fechada no grafo de equações até a incógnita alvo.
          </p>
        </div>
      </div>

      {/* Unblocking Clues (Missing Grandezas) */}
      {unblockingGrandezas && unblockingGrandezas.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-paper-50 p-3.5 dark:border-study-line dark:bg-study-card space-y-2">
          <span className="text-[11px] font-bold uppercase tracking-wider text-zinc-600 dark:text-zinc-300 block">
            Grandezas que destravam a resolução (menor número de etapas):
          </span>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
            {unblockingGrandezas.map(({ variable, stepsNeeded, unblocksVia }) => (
              <div
                key={variable.id}
                className="flex items-center justify-between rounded border border-paper-200 bg-paper-100 p-2 text-xs dark:border-study-line dark:bg-study-panel"
              >
                <div className="truncate mr-2">
                  <span className="font-mono font-bold text-indigo-600 dark:text-indigo-400 mr-1.5">
                    <InlineLatex>{variable.symbol}</InlineLatex>
                  </span>
                  <span className="font-medium text-zinc-800 dark:text-zinc-200">{variable.name}</span>
                  <span className="text-[10px] text-zinc-400 block truncate">
                    Resolve em {stepsNeeded} etapa{stepsNeeded > 1 ? 's' : ''} via {unblocksVia}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => onAddKnown(variable.id)}
                  className="shrink-0 flex items-center gap-1 rounded bg-indigo-600 px-2 py-1 text-[10px] font-bold text-white hover:bg-indigo-700 transition"
                >
                  <PlusCircle className="h-3 w-3" />
                  + Adicionar
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
